import { frontendURL } from '../../../helper/URLHelper';
import store from '../../../store/index';
import {
  resolveSubscriptionAccess,
  hasHighTierAccess,
} from '../../../helper/subscriptionHelper';
import { resolveModuleName } from '../../../helper/moduleGuards';
import ProjectsList from './ProjectsList.vue';
import ProjectDetail from './ProjectDetail.vue';
import PropertyAppointments from './PropertyAppointments.vue';
import AgentsList from './AgentsList.vue';
import AgentCalendar from './calendar/AgentCalendar.vue';

// Module restriction guard for the Real Estate module — mirrors
// organization.routes.js's moduleRestrictedGuard: active subscription +
// pro/enterprise tier + this module specifically active. Modules are
// mutually exclusive (the knowledge service only ever returns one active
// module per account), so this also guarantees no other module's routes
// are reachable at the same time.
const moduleAccessGuard = async (to, from, next) => {
  if (process.env.CYPRESS_TEST_BUILD === 'true' && window.Cypress) {
    return next();
  }

  const accountId = to.params.accountId;

  const { hasActiveSubscription, hasError } = await resolveSubscriptionAccess(
    store,
    accountId
  );
  if (hasError) {
    return next({
      name: 'subscription_error',
      params: { accountId },
      query: { redirect: to.fullPath },
    });
  }
  if (!hasActiveSubscription) {
    return next({ name: 'pricing_table', params: { accountId } });
  }

  const moduleName = await resolveModuleName(store, accountId);
  if (!hasHighTierAccess(store) || moduleName !== 'real_estate') {
    return next({ name: 'dashboard', params: { accountId } });
  }

  return next();
};

export default {
  routes: [
    {
      path: frontendURL('accounts/:accountId/real_estate'),
      component: ProjectsList,
      name: 'real_estate',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: moduleAccessGuard,
    },
    {
      // Must come before the :projectId route below so the literal
      // "appointments" path segment isn't swallowed as a project id.
      path: frontendURL('accounts/:accountId/real_estate/appointments'),
      component: PropertyAppointments,
      name: 'real_estate_appointments',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: moduleAccessGuard,
    },
    {
      // Must come before the :projectId route below so the literal
      // "agents" path segment isn't swallowed as a project id.
      path: frontendURL('accounts/:accountId/real_estate/agents'),
      component: AgentsList,
      name: 'real_estate_agents',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: moduleAccessGuard,
    },
    {
      // Must come before the :projectId route below so the literal
      // "calendar" path segment isn't swallowed as a project id.
      path: frontendURL('accounts/:accountId/real_estate/calendar'),
      component: AgentCalendar,
      name: 'real_estate_calendar',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: moduleAccessGuard,
    },
    {
      path: frontendURL('accounts/:accountId/real_estate/:projectId'),
      component: ProjectDetail,
      name: 'real_estate_project',
      meta: {
        permissions: ['administrator'],
      },
      beforeEnter: moduleAccessGuard,
    },
  ],
};
